import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  Product,
} from '../store/services/products';
import { useGetProductCategoriesQuery } from '../store/services/productCategories';
import { useGetCompaniesQuery } from '../store/services/companies';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  category_id: yup.string().required('Category is required'),
  company_id: yup.string().required('Company is required'),
  unit: yup.string().required('Unit is required'),
  description: yup.string().required('Description is required'),
});

interface ProductFormValues {
  name: string;
  category_id: string;
  company_id: string;
  unit: string;
  description: string;
}

export default function Products() {
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // Get the current user from Redux state
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  // Check if user has permission to add/edit/delete
  const hasAdminPermission = currentUser?.role === 'SuperAdmin';
  
  const { data: products, isLoading, refetch } = useGetProductsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: categories } = useGetProductCategoriesQuery();
  const { data: companies } = useGetCompaniesQuery();
  
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const [deleteProduct] = useDeleteProductMutation();

  const formik = useFormik<ProductFormValues>({
    initialValues: {
      name: '',
      category_id: '',
      company_id: '',
      unit: '',
      description: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedProduct) {
          await updateProduct({
            id: selectedProduct._id,
            data: values,
          }).unwrap();
          setSnackbar({ open: true, message: 'Product updated successfully', severity: 'success' });
        } else {
          await createProduct(values).unwrap();
          setSnackbar({ open: true, message: 'Product created successfully', severity: 'success' });
        }
        handleClose();
        refetch();
      } catch (error) {
        console.error('Failed to save product:', error);
        setSnackbar({ open: true, message: 'Failed to save product', severity: 'error' });
      }
    },
  });

  const handleClickOpen = () => {
    setSelectedProduct(null);
    formik.resetForm();
    setOpen(true);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    formik.setValues({
      name: product.name,
      category_id: product.category_id,
      company_id: product.company_id,
      unit: product.unit,
      description: product.description,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id).unwrap();
        setSnackbar({ open: true, message: 'Product deleted successfully', severity: 'success' });
        refetch();
      } catch (error) {
        console.error('Failed to delete product:', error);
        setSnackbar({ open: true, message: 'Failed to delete product', severity: 'error' });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Products</h2>
        {hasAdminPermission && (
          <Button variant="contained" onClick={handleClickOpen}>
            Add Product
          </Button>
        )}
      </Box>
      
      {!hasAdminPermission && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access to products.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Description</TableCell>
              {hasAdminPermission && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {products?.map((product) => {
              const category = categories?.find(cat => cat._id === product.category_id);
              const company = companies?.find(comp => comp._id === product.company_id);
              return (
                <TableRow key={product._id}>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{category?.name || '-'}</TableCell>
                  <TableCell>{company?.name || '-'}</TableCell>
                  <TableCell>{product.unit}</TableCell>
                  <TableCell>{product.description}</TableCell>
                  {hasAdminPermission && (
                    <TableCell align="right">
                      <IconButton onClick={() => handleEdit(product)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(product._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth
              margin="dense"
              id="category_id"
              name="category_id"
              select
              label="Category"
              value={formik.values.category_id}
              onChange={formik.handleChange}
              error={formik.touched.category_id && Boolean(formik.errors.category_id)}
              helperText={formik.touched.category_id && formik.errors.category_id}
            >
              {categories?.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="dense"
              id="company_id"
              name="company_id"
              select
              label="Company"
              value={formik.values.company_id}
              onChange={formik.handleChange}
              error={formik.touched.company_id && Boolean(formik.errors.company_id)}
              helperText={formik.touched.company_id && formik.errors.company_id}
            >
              {companies?.map((company) => (
                <MenuItem key={company._id} value={company._id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="dense"
              id="unit"
              name="unit"
              label="Unit"
              value={formik.values.unit}
              onChange={formik.handleChange}
              error={formik.touched.unit && Boolean(formik.errors.unit)}
              helperText={formik.touched.unit && formik.errors.unit}
            />
            <TextField
              fullWidth
              margin="dense"
              id="description"
              name="description"
              label="Description"
              multiline
              rows={4}
              value={formik.values.description}
              onChange={formik.handleChange}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedProduct ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}